// Yesterday on the things you follow — newest first (item #650, TCSD-0366 ruling 5).
// Opening the list marks what it shows as read, which is what clears the grey dot
// on the bell. A building name opens the building's card.
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../shared/supabase';
import { refreshReaders } from '../../shared/save-path';
import { NEWS_LABEL, useMyNews, useNewsPrefs } from './news';

const time = (iso: string) =>
  new Date(iso).toLocaleString('en-US', { timeZone: 'America/Chicago', weekday: 'short', hour: 'numeric', minute: '2-digit' });

export default function NewsPage() {
  const prefs = useNewsPrefs();
  const off = prefs.loaded && prefs.cadence === 'off';
  const { items, loaded } = useMyNews(prefs.cadence, prefs.loaded && !off);
  const marked = useRef(false);

  useEffect(() => {
    if (!loaded || marked.current) return;
    const unread = items.filter((i) => !i.read_at).map((i) => i.recipient_row_id);
    if (unread.length === 0) return;
    marked.current = true;
    void supabase
      .rpc('notify_triage', { p_recipient_row_ids: unread, p_action: 'read' })
      .then(({ error }) => {
        if (!error) refreshReaders(['notification_recipients']);
      });
  }, [loaded, items]);

  return (
    <div className="min-h-full bg-tcs-bg-muted">
      <div className="mx-auto max-w-xl px-4 pb-16 pt-3">
        <Link to="/my-work" className="inline-flex min-h-[44px] items-center text-[14px] text-tcs-accent-ink">
          ‹ My Work
        </Link>
        <div className="mb-1.5 flex items-baseline justify-between px-0.5 text-[11px] font-bold uppercase tracking-[0.07em] text-tcs-text-muted">
          <span>{prefs.cadence === 'weekly' ? 'The past week' : 'Yesterday'}</span>
          <span className="font-medium normal-case tracking-normal">newest first</span>
        </div>
        {off ? (
          <p className="mt-4 text-center text-[13px] text-tcs-text-muted">
            News is off. <Link className="underline" to="/my-work/notifications">Change it</Link>.
          </p>
        ) : !loaded ? (
          <p className="mt-4 text-center text-[13px] text-tcs-text-muted">Loading…</p>
        ) : items.length === 0 ? (
          <p className="mt-4 text-center text-[13px] text-tcs-text-muted">Nothing happened on what you follow.</p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-tcs-border bg-tcs-bg">
            {items.map((n) => {
              const body = (
                <>
                  <span className="min-w-0">
                    <span className={`block text-[13.5px] font-medium ${n.entity_type === 'facility' ? 'text-tcs-accent-ink' : 'text-tcs-text'}`}>
                      {n.title}
                      {n.entity_type === 'facility' ? ' ›' : ''}
                    </span>
                    <span className="block text-[12px] text-tcs-text-secondary">{NEWS_LABEL[n.news_kind] ?? ''}</span>
                  </span>
                  <span className="shrink-0 text-[12px] tabular-nums text-tcs-text-muted">{time(n.created_at)}</span>
                </>
              );
              const cls = 'flex min-h-[48px] w-full items-start justify-between gap-2 border-b border-tcs-border px-3 py-2 text-left last:border-b-0';
              return n.entity_type === 'facility' ? (
                <Link key={n.recipient_row_id} to={`/account-management/facilities/${n.entity_id}`} className={cls}>
                  {body}
                </Link>
              ) : (
                <div key={n.recipient_row_id} className={cls}>
                  {body}
                </div>
              );
            })}
          </div>
        )}
        <p className="mt-3 text-center text-[12px] text-tcs-text-muted">Tap a building to open its card.</p>
      </div>
    </div>
  );
}
